import { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../_redux/notice-groups/NoticeGroupsAction";
import {
  DeleteButton,
  ExportButton,
  FormButton,
  copy,
  deletionAlert,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  resultAlert
} from "../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { useNoticeGroupsUIContext } from "./NoticeGroupsUIContext";
import { NoticeGroupsFilter } from "./notice-groups-filter/NoticeGroupsFilter";
import { NoticeGroupsTable } from "./notice-groups-table/NoticeGroupsTable";

export function NoticeGroupsCard() {
  // Notice Groups UI Context
  const noticeGroupsUIContext = useNoticeGroupsUIContext();
  const noticeGroupsUIProps = useMemo(() => {
    return {
      ids: noticeGroupsUIContext.ids,
      setIds: noticeGroupsUIContext.setIds,
      queryParams: noticeGroupsUIContext.queryParams,
      openNewNoticeGroupPage: noticeGroupsUIContext.openNewNoticeGroupPage,
    };
  }, [noticeGroupsUIContext]);

  // Getting curret state of Notice Group list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.noticeGroups }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isAdditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);
  
  // Notice Groups Redux state
  const dispatch = useDispatch();
   
  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Duyuru Grubu Listesini Excele Aktarır */
  const exportNoticeGroupsToExcel = () => {
    dispatch(actions.getAllNoticeGroups(noticeGroupsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [30, 45];
      const columns = ["Adı", "Açıklaması"];
      entities.forEach(entity => rows.push([entity.name, entity.definition]));
      const filename = "Duyuru Grubu Listesi"
      exportToExcel({ columns, rows, widths, filename });
    });
  }

  /** Belli Bir Duyuru Grubu Listesini PDF e Aktarır */
  const exportNoticeGroupsToPDF = () => {
    dispatch(actions.getAllNoticeGroups(noticeGroupsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [
        {
          header: "Adı",
          rows: entities.map(entity => entity.name)
        },
        {
          header: "Açıklaması",
          rows: entities.map(entity => entity.definition)
        }
      ];
  
      const filename = "Duyuru Grubu Listesi";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Duyuru Grubu Listesini Yazdırma Penceresini Açar */
  const printNoticeGroups = () => {
    dispatch(actions.getAllNoticeGroups(noticeGroupsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = "Duyuru Grubu Listesi";
      const properties = ["Adı", "Açıklaması"];

      entities.forEach(entity => {
        data.push({
          "Adı": entity.name,
          "Açıklaması": entity.definition ?? ""
        });
      });

      print({ data, properties, header });
    });
  }

  /** Belli Bir Duyuru Grubu Listesini Panoya Kopyalar */
  const copyNoticeGroups = () => {
    dispatch(actions.getAllNoticeGroups(noticeGroupsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const header = "Duyuru Grubu Listesi";
      const columns = ["Adı", "Açıklaması"];
      entities.forEach(entity => rows.push([entity.name, entity.definition ?? ""]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Duyuru Grubu Listesi Siliniyor */
  const deleteNoticeGroups = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
  
    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }  
    deletionAlert().then(result => {
      if (result.isConfirmed) dispatch(actions.deleteNoticeGroups(ids)).then(result => onResult(result));
    });
  }

  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
  
    if (isSuccess) {
      setTimeout(() => {
        noticeGroupsUIProps.setIds([]);
        dispatch(actions.getNoticeGroups(noticeGroupsUIProps.queryParams));      
      }, 2500);
    }
  };
  
  const title = <>Duyuru Grubu Listesi{noticeGroupsUIProps.ids.length > 0 ? 
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{noticeGroupsUIProps.ids.length}</span>]</> :
                   ""}</>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isAdditionAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportNoticeGroupsToExcel}
                exportToPDF={exportNoticeGroupsToPDF}
                print={printNoticeGroups}
                copy={copyNoticeGroups}
              />
            }
            {
              isAdditionAuthorized &&
              <FormButton 
                path="Navigation/Plus"
                title="Yeni Duyuru Grubu"
                method={noticeGroupsUIProps.openNewNoticeGroupPage}
              />
            }              
            {
              isDeletionAuthorized &&
              <DeleteButton
                title="Seçilenleri Sil"
                method={() => deleteNoticeGroups(noticeGroupsUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }        
      </CardHeader>
      <CardBody>
        <NoticeGroupsFilter />
        <NoticeGroupsTable 
          deleteNoticeGroups={deleteNoticeGroups}
        />
      </CardBody>
    </Card>
  );
}